/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MenuProps, notification } from 'antd';
import {
  loadLanguageData,
  supportedLanguages,
} from 'queries/ossix/languages/fetchSupportedLanguages';
import {
  IComponentProps,
  ILanguage,
  ILanguageData,
} from 'interfaces/queries/language';
import { LanguageImg } from 'components/ossix/LangComponent';
import usePreferredLang from './preferredLang';

import 'dayjs/locale/fr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import { requestSaveUserLanguage } from 'queries/ossix/user/userData';
import Toast from 'components/shared/Alert';
import { localTranslationsWords } from 'constants/localTranslations';

dayjs.extend(localizedFormat);
dayjs.locale('en');

interface LanguageContextProps {
  translate: (_text?: string, _index?: number | string) => string;
  menuProps: MenuProps;
  currentLang: ICurrentLang;
  isLoading: boolean;
  isLoadingLanguage: boolean;
  currentLanguage: string | null;
  formatDate: (_date: string) => any;
  menuSaveLangProps: MenuProps;
  loadSavedLang: boolean;
}

interface ICurrentLang {
  key: number | string;
  label: string;
  icon: React.ReactNode;
}

const defaultLang = 'en';
const LanguageContext = createContext<LanguageContextProps | null>(
  null,
);

const LanguageProvider: React.FC<IComponentProps> = ({
  children,
}) => {
  const {
    currentLanguage,
    savePreferredLang,
    currentLanguageData,
    savePreferredLangData,
  } = usePreferredLang();

  const {
    loadSavedLang,
    isSaveLangSuccess,
    isSaveLanguageError,
    requestSaveLang,
    saveLangResponse,
  } = requestSaveUserLanguage();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    currentLanguage || defaultLang,
  );

  const [api, contextHolder] = notification.useNotification();

  const { language, isLoading } = supportedLanguages({});

  const {
    isSuccess,
    languageData,
    isLoadingLanguage,
    submitData: requestPayload,
  } = loadLanguageData();

  const applyLanguageAndReload = (langCode: string) => {
    savePreferredLang(langCode);

    if (langCode === 'fr') {
      const existing = localStorage.getItem('currentLangData');
      let currentArr: any[] = [];
      try {
        currentArr = JSON.parse(existing || '[]') || [];
      } catch (e) {
        currentArr = [];
      }
      const mergedMap = new Map();
      [...currentArr, ...localTranslationsWords].forEach((item) => {
        if (item?.Default) {
          mergedMap.set(
            item.Default.toLowerCase().replace(/\s+/g, ' ').trim(),
            item,
          );
        }
      });
      savePreferredLangData(
        JSON.stringify(Array.from(mergedMap.values())),
      );
    } else if (langCode === defaultLang) {
      savePreferredLang(defaultLang);
    }
  };

  const handleMenuClick: MenuProps['onClick'] = (value) => {
    if (value.key === currentLanguage) return;
    applyLanguageAndReload(value.key);
    window.location.reload();
  };

  const handleSaveLang: MenuProps['onClick'] = (value) => {
    if (value.key === currentLanguage) return;
    applyLanguageAndReload(value.key);
    const saveUserLangPayload = {
      LanguageCode: value.key,
    };
    try {
      requestSaveLang(saveUserLangPayload, {
        onSettled: () => {
          window.location.reload();
        },
      });
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch {
      window.location.reload();
    }
  };

  const items: MenuProps['items'] | undefined = language?.map(
    (item: ILanguage) => ({
      key: item.Code,
      label: item.Name,
      icon: <LanguageImg flag={item.Flag} />,
    }),
  );

  const currentLang: any =
    items && items.find((lang) => lang?.key === selectedLanguage);

  useEffect(() => {
    if (defaultLang != selectedLanguage && selectedLanguage != null) {
      requestPayload({ Language: selectedLanguage });
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (isSaveLangSuccess && selectedLanguage) {
      requestPayload({ Language: selectedLanguage });
    }
    if (isSaveLanguageError) {
      Toast(api, 'Error', saveLangResponse);
    }
  }, [isSaveLangSuccess, saveLangResponse, selectedLanguage]);

  useEffect(() => {
    const checkDefaultLang = defaultLang != currentLanguage;
    const isFrench = currentLanguage === 'fr';

    if (checkDefaultLang && isSuccess && languageData) {
      if (isFrench) {
        const combinedData = [
          ...languageData,
          ...localTranslationsWords,
        ];
        savePreferredLangData(JSON.stringify(combinedData));
      } else {
        savePreferredLangData(JSON.stringify(languageData));
      }
    }

    if (currentLanguage) {
      setSelectedLanguage(currentLanguage);
    }
  }, [currentLang, currentLanguage, languageData]);

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const menuSaveLangProps = {
    items,
    onClick: handleSaveLang,
  };

  const normalize = (str: string) =>
    str.toLowerCase().replace(/\s+/g, ' ').trim();

  const translate = (text = '', index?: number | string): string => {
    if (defaultLang === currentLanguage) {
      return text;
    }

    try {
      const searchValue = index ? Number(index) : normalize(text);

      let preferredLangData: any = null;

      if (currentLanguage === 'fr') {
        preferredLangData = localTranslationsWords.find(
          (item) =>
            item.Index === searchValue ||
            normalize(item.Default) === searchValue,
        );
      }

      if (!preferredLangData) {
        preferredLangData = currentLanguageData?.find(
          (item: ILanguageData) =>
            item.Index === searchValue ||
            normalize(item.Default) === searchValue,
        );
      }

      return preferredLangData?.Label ?? text;
    } catch (error) {
      return text;
    }
  };

  dayjs.extend(localizedFormat);
  dayjs.locale(currentLanguage || 'en');

  const formatDate = (dateString: string) => {
    const datePart = dateString?.split(' - ')[0];
    return dayjs(datePart).format('LL');
  };

  return (
    <LanguageContext.Provider
      value={{
        translate,
        menuProps,
        currentLang,
        isLoading,
        isLoadingLanguage,
        currentLanguage,
        formatDate,
        loadSavedLang,
        menuSaveLangProps,
      }}
    >
      {children}
      {contextHolder}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const languageContext = useContext(LanguageContext);
  if (!languageContext) {
    throw new Error('Platform configuration not available');
  }
  return languageContext;
};

export { useLanguage, LanguageProvider };
